import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { LettoreSse } from '@shared/utils/sse-client.util';
import { AuthService } from './auth.service';

export interface VideoPronto {
  url: string;
  poster: string;
  durata: number;
  larghezza: number;
  altezza: number;
}

export type StatoCaricamento =
  | { fase: 'carico'; percentuale: number }
  | { fase: 'in-coda'; davanti: number }
  | { fase: 'converto'; percentuale: number }
  | { fase: 'pronto'; video: VideoPronto };

/**
 * Dominio dei video, fuori dal proxy Cloudflare: li' un caricamento si
 * fermerebbe a 100 MB per richiesta, e servire video dalla CDN proxata e'
 * contro le condizioni d'uso di Cloudflare.
 */
const BASE = 'https://media.infooggi.it/api/video';

@Injectable({ providedIn: 'root' })
export class VideoService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  /**
   * Carica il file e segue la conversione fino al video pronto.
   *
   * Sono due richieste perche' nessuna delle due sa fare il lavoro dell'altra:
   * il progresso in byte lo da' solo HttpClient con reportProgress, e solo una
   * risposta letta a pezzi puo' raccontare la conversione mentre avviene.
   */
  carica(file: File): Observable<StatoCaricamento> {
    return new Observable<StatoCaricamento>((subscriber) => {
      const form = new FormData();
      form.append('file', file);
      const controller = new AbortController();

      const sottoscrizione = this.http
        .post<{ id: string }>(`${BASE}/upload`, form, { reportProgress: true, observe: 'events' })
        .subscribe({
          next: (evento) => {
            if (evento.type === HttpEventType.UploadProgress && evento.total) {
              subscriber.next({ fase: 'carico', percentuale: Math.round((evento.loaded / evento.total) * 100) });
            } else if (evento.type === HttpEventType.Response && evento.body?.id) {
              void this.seguiConversione(evento.body.id, subscriber, controller.signal);
            }
          },
          error: (err) => subscriber.error(err),
        });

      return () => {
        sottoscrizione.unsubscribe();
        controller.abort();
      };
    });
  }

  private async seguiConversione(id: string, subscriber: any, signal: AbortSignal): Promise<void> {
    let risposta: Response;
    try {
      risposta = await fetch(`${BASE}/${id}/stato`, {
        headers: { 'x-auth-token': this.authService.token },
        signal,
      });
    } catch (err) {
      subscriber.error({ status: 0, error: err });
      return;
    }
    if (!risposta.ok || !risposta.body) {
      subscriber.error({ status: risposta.status, error: null });
      return;
    }

    const lettore = new LettoreSse();
    const reader = risposta.body.getReader();
    const decoder = new TextDecoder();
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        for (const evento of lettore.aggiungi(decoder.decode(value, { stream: true }))) {
          if (evento.nome === 'errore') {
            subscriber.error({ status: 502, error: { messaggio: evento.dati?.messaggio } });
            return;
          }
          if (evento.nome === 'in-coda') {
            subscriber.next({ fase: 'in-coda', davanti: evento.dati?.davanti ?? 0 });
          } else if (evento.nome === 'conversione') {
            subscriber.next({ fase: 'converto', percentuale: evento.dati?.percentuale ?? 0 });
          } else if (evento.nome === 'pronto') {
            subscriber.next({ fase: 'pronto', video: evento.dati as VideoPronto });
            subscriber.complete();
            return;
          }
        }
      }
      // Lo stream e' finito senza dire com'e' andata: il video non e' pronto.
      subscriber.error({ status: 0, error: null });
    } catch (err) {
      if (!signal.aborted) {
        subscriber.error({ status: 0, error: err });
      }
    }
  }
}
